import React, { Component, ReactNode } from "react";
import { Box, Alert, Text, Stack } from "@mantine/core";

export interface ContentBlockErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;

  /** Optional fallback component to render on error */
  fallback?: ReactNode;

  /** Additional CSS class name for styling */
  className?: string;

  /** Callback fired when an error occurs */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ContentBlockErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ContentBlockErrorBoundary extends Component<
  ContentBlockErrorBoundaryProps,
  ContentBlockErrorBoundaryState
> {
  constructor(props: ContentBlockErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(
    error: Error
  ): ContentBlockErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging
    console.error(
      "ContentBlockErrorBoundary caught an error:",
      error,
      errorInfo
    );

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  override componentDidUpdate(prevProps: ContentBlockErrorBoundaryProps) {
    // Reset error state when children change (new content block)
    if (prevProps.children !== this.props.children && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  override render() {
    if (this.state.hasError) {
      // If custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error fallback UI
      return (
        <Box
          className={`content-block-error-boundary ${
            this.props.className || ""
          }`}
          h="100%"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "var(--mantine-spacing-lg)",
          }}
        >
          <Stack align="center" gap="md" maw={400}>
            <Alert
              color="red"
              title="Content Block Error"
              className="content-block-error-boundary__alert"
            >
              <Text size="sm" className="content-block-error-boundary__message">
                This content block could not be displayed due to an error.
              </Text>
              {process.env.NODE_ENV === "development" && this.state.error && (
                <Text
                  size="xs"
                  c="dimmed"
                  mt="xs"
                  className="content-block-error-boundary__debug"
                  style={{ fontFamily: "monospace" }}
                >
                  {this.state.error.message}
                </Text>
              )}
            </Alert>
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}
